import { useFlag } from '@calendly/feature-check-react'
import { useOnValueChange } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { FilterTypes } from 'constants/meetings'
import { useGetUserSettingsQuery, usePatchUserSettingsMutation } from 'features/availability'
import { mapChangedFiltersForAnalytics, MeetingsFilterSourceSection } from 'features/meetings/index'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import { useFilters } from 'hooks/meetings/filters'

import { BaseFilterModal } from './base_filter_modal'
import { BufferViewCheckbox } from './components/buffer_view_checkbox'
import { DateRange } from './components/date_range'
import { EventTypesSelect } from './components/event_types_select'
import { FilterSectionGroup } from './components/filter_section_group'
import { InviteeEmailSelect } from './components/invitee_email_select'
import { NotetakerRecap } from './components/notetaker_recap'
import { PackagesSelect } from './components/packages_select'
import { Role } from './components/role'
import { Status } from './components/status'
import { TeamsSelect } from './components/teams_select'
import { TrackingSelect } from './components/tracking_select'
import { UsersSelect } from './components/users_select'
import { PresetFilterSheetLayoutProvider } from './preset_filter_sheet_layout_context'
import { presetFilterSectionHeaderAfterDividerCss } from './preset_filter_sheet_styles'
import { changedMeetingFilters, hasMeetingsPageSettingsChanged, buildMeetingsFilterApplyParams } from './utils'

const AdvancedFilters = ({
  newFilterParams,
  setNewFilterParams,
  isFilterVisible,
  notetakerEnabled,
  hidePackages = false,
  useShortStatusLabel = false,
}) => (
  <>
    {!hidePackages && (
      <PackagesSelect params={newFilterParams} onApply={setNewFilterParams} useColumnLayout />
    )}
    {isFilterVisible(FilterTypes.TEAMS) && (
    <TeamsSelect
      params={newFilterParams}
      onApply={setNewFilterParams}
      useColumnLayout
    />
    )}
    {isFilterVisible(FilterTypes.USERS) && (
    <UsersSelect
      params={newFilterParams}
      onApply={setNewFilterParams}
      useColumnLayout
    />
    )}
    {isFilterVisible(FilterTypes.STATUS) && (
      <Status
        params={newFilterParams}
        onChange={setNewFilterParams}
        useColumnLayout
        useShortLabel={useShortStatusLabel}
      />
    )}
    {notetakerEnabled && isFilterVisible(FilterTypes.NOTETAKER_RECAP) && (
      <NotetakerRecap
        params={newFilterParams}
        onChange={setNewFilterParams}
        useColumnLayout
      />
    )}
    {isFilterVisible(FilterTypes.ROLE) && (
      <Role
        params={newFilterParams}
        onChange={setNewFilterParams}
        useColumnLayout
      />
    )}
    {isFilterVisible(FilterTypes.TRACKING_SELECT) && (
    <TrackingSelect
      params={newFilterParams}
      onApply={setNewFilterParams}
      useColumnLayout
    />
    )}
  </>
)

export const MeetingsFilterModal = ({
  hiddenFilters,
  filterDefaults = {},
  onClose,
  showBuffers: showBuffersProp,
  setShowBuffers: setShowBuffersProp,
  useResultsCount,
  location,
  resetParams,
  filterParams,
  isMeetingsPage,
  isMyCalendlyScope,
  notetakerEnabled
}) => {
  const [newFilterParams, setNewFilterParams] = useState(filterParams)
  const isFilterVisible = useCallback((controlType) => !hiddenFilters?.includes?.(controlType), [hiddenFilters])
  const { applyFilterParams, isValidApplyParams } = useFilters()
  const dispatch = useDispatch()
  const track = useTrack()
  const showBufferToggle = isFilterVisible(FilterTypes.BUFFER_VIEW)
  const [showError, setShowError] = useState(false)
  const { data: userSettings } = useGetUserSettingsQuery()
  const [updateUserSettings] = usePatchUserSettingsMutation()
  const showBuffersFromApi = userSettings?.meetingsPageSettings?.showBuffers ?? true
  // Use prop if provided (e.g., from calendar context), otherwise use API value
  const showBuffersSource = showBuffersProp ?? showBuffersFromApi
  const [localShowBuffers, setLocalShowBuffers] = useState(showBuffersSource)
  const { count: resultCount, isLoading: isResultCountLoading } = useResultsCount(newFilterParams, false)
  const changedFilters = changedMeetingFilters(isMeetingsPage, isMyCalendlyScope, newFilterParams)
  const userUuid = userSettings?.userUuid

  useOnValueChange(filterParams.search_keyword, () => setNewFilterParams(filterParams))

  const sendAnalytics = () => {
    const searchType = newFilterParams.search_keyword ? 'text' : 'apply_filter'
    const properties = {
      results_count: resultCount,
      search_type: searchType,
      location: 'meetings_page',
      filters: mapChangedFiltersForAnalytics(changedFilters),
      ...(searchType === 'text' && {
        search_text: newFilterParams.search_keyword
      })
    }
    track({
      name: TRACKING_EVENT_NAMES.SEARCH_PERFORMED,
      properties
    })
  }

  useEffect(() => {
    if (!isValidApplyParams(newFilterParams)) {
      setShowError(true)
    } else {
      setShowError(false)
    }
  }, [newFilterParams, isValidApplyParams])

  const presetFlagOn = useFlag('sa_default_preset_toggle', false)
  const isPhone = useDeviceType('isPhone')
  const usePresetFilterLayout = presetFlagOn && isPhone
  /*
   * The preset chip row above the meetings list replaces the picker's legacy presets, so drop them
   * only where that row actually renders: the meetings page, behind the same flag. Everywhere else
   * (recaps, single-use links) the picker is the only place to pick a preset range.
   */
  const showLegacyDatePresets = !(isMeetingsPage && presetFlagOn)

  const primaryFilterFields = (
    <>
      <InviteeEmailSelect params={newFilterParams} onApply={setNewFilterParams} useColumnLayout />
      <EventTypesSelect params={newFilterParams} onApply={setNewFilterParams} useColumnLayout />
      {usePresetFilterLayout && (
        <PackagesSelect params={newFilterParams} onApply={setNewFilterParams} useColumnLayout />
      )}
    </>
  )

  const onApply = () => {
    if (!showError) {
      sendAnalytics()
      const paramsForApply = isMeetingsPage
        ? buildMeetingsFilterApplyParams(newFilterParams, {
          presetFlagOn,
          hasChangedFilters: changedFilters.length > 0,
        })
        : newFilterParams
      dispatch(applyFilterParams(paramsForApply))

      if (setShowBuffersProp) {
        setShowBuffersProp(localShowBuffers)
      }

      if (isMeetingsPage && userUuid) {
        const { meetingsPageSettings } = userSettings
        const newMeetingsPageSettings = {
          showBuffers: localShowBuffers,
          schedulingMethods: newFilterParams.scheduling_methods || []
        }

        if (hasMeetingsPageSettingsChanged(meetingsPageSettings, newMeetingsPageSettings)) {
          updateUserSettings({
            userUuid,
            user_settings: {
              meetingsPageSettings: newMeetingsPageSettings
            },
            skipToast: true
          })
        }
      }

      onClose()
    }
  }

  const onClear = () => {
    setNewFilterParams({ ...resetParams, ...filterDefaults })
    setLocalShowBuffers(showBuffersSource)
  }

  return (
    <PresetFilterSheetLayoutProvider value={usePresetFilterLayout}>
      <BaseFilterModal
        onClose={onClose}
        onApply={onApply}
        onClear={onClear}
        hasError={showError}
        fullScreenSheet={usePresetFilterLayout}
        advancedFilters={(
          <AdvancedFilters
            newFilterParams={newFilterParams}
            setNewFilterParams={setNewFilterParams}
            isFilterVisible={isFilterVisible}
            notetakerEnabled={notetakerEnabled}
            hidePackages={usePresetFilterLayout}
            useShortStatusLabel={usePresetFilterLayout}
          />
        )}
        isResultsLoading={isResultCountLoading}
        resultCount={resultCount}
      >
        {showBufferToggle && !isMeetingsPage && (
          <BufferViewCheckbox
            showBuffers={localShowBuffers}
            toggleShowBuffers={setLocalShowBuffers}
          />
        )}
        {usePresetFilterLayout && (
          <MeetingsFilterSourceSection
            schedulingMethods={newFilterParams.scheduling_methods}
            onSchedulingMethodsChange={(methods) => (
              setNewFilterParams((prev) => ({ ...prev, scheduling_methods: methods }))
            )}
          />
        )}
        {!usePresetFilterLayout && (
          <DateRange
            params={newFilterParams}
            onApply={setNewFilterParams}
            useColumnLayout
            showPresets={showLegacyDatePresets}
          />
        )}
        {usePresetFilterLayout ? (
          <FilterSectionGroup
            label={I18n.t('publisher.meetings.filter_modal.filters.label')}
            headerClassName={presetFilterSectionHeaderAfterDividerCss}
          >
            {primaryFilterFields}
          </FilterSectionGroup>
        ) : (
          primaryFilterFields
        )}
      </BaseFilterModal>
    </PresetFilterSheetLayoutProvider>
  )
}
